import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from 'Context';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { handleDropboxOAuthCallback } from 'Pages/cloudUpload/utils/dropboxOAuthHandler';
import { handleGoogleDriveOAuthCallback } from 'Pages/cloudUpload/utils/googleDriveOAuthHandler';
import { SettingsPage } from '../components/page';
import { DataView } from '../components/data-view';
import styles from './index.module.css';

// Dropbox SVG Icon
const DropboxIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 1.807L0 5.629l6 3.822 6.001-3.822L6 1.807zM18 1.807l-6 3.822 6 3.822 6-3.822-6-3.822zM0 13.274l6 3.822 6.001-3.822L6 9.452l-6 3.822zM18 9.452l-6 3.822 6 3.822 6-3.822-6-3.822zM6 18.371l6.001 3.822 6-3.822-6-3.822L6 18.371z" />
  </svg>
);

// Google Drive SVG Icon
const GoogleDriveIcon = () => (
  <svg
    className={styles.menuIcon}
    width="20"
    height="20"
    viewBox="0 0 87.3 78"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
  >
    <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" />
    <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" />
    <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" />
    <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" />
    <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" />
    <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" />
  </svg>
);

const cloudServices = [
  {
    id: 'dropbox',
    name: 'Dropbox',
    icon: DropboxIcon,
    color: '#0061FF',
    enabled: true,
  },
  {
    id: 'googledrive',
    name: 'Google Drive',
    icon: GoogleDriveIcon,
    color: '#4285F4',
    enabled: true,
  },
];

export const CloudSettings = () => {
  const { setAppSnackbar, langText } = useContext(AppContext);
  const navigate = useNavigate();

  // Initialize state from localStorage
  const [connectedServices, setConnectedServices] = useState(() => {
    const saved = localStorage.getItem('cloudConnectedServices');
    return saved ? JSON.parse(saved) : { dropbox: null, googledrive: null };
  });

  const handleServiceConnect = (service, connectionData) => {
    const newServices = {
      ...connectedServices,
      [service]: connectionData,
    };
    setConnectedServices(newServices);
    localStorage.setItem('cloudConnectedServices', JSON.stringify(newServices));
    setAppSnackbar({
      isVisible: true,
      message: `Successfully connected to ${service === 'googledrive' ? 'Google Drive' : 'Dropbox'}`,
      type: 'success',
    });
  };

  const handleConnect = async (serviceId) => {
    if (serviceId === 'dropbox') {
      const clientId = 'xycymyua8j9yykw';
      const redirectUri = `${window.location.origin}/settings/cloud`;
      const state = Math.random().toString(36).substring(7);

      sessionStorage.setItem('dropboxOAuthState', state);

      const logoutUrl = 'https://www.dropbox.com/logout';
      const authUrl = `https://www.dropbox.com/oauth2/authorize?client_id=${clientId}&response_type=token&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}&force_reapprove=true&force_reauthentication=true`;

      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = logoutUrl;
      document.body.appendChild(iframe);

      setTimeout(() => {
        document.body.removeChild(iframe);
        window.location.href = authUrl;
      }, 1000);
    } else if (serviceId === 'googledrive') {
      const clientId = '526946985013-7ol8pss03r4cd6dqbijso7a4pfrgnhsi.apps.googleusercontent.com';
      const redirectUri = `${window.location.origin}/settings/cloud`;
      const state = Math.random().toString(36).substring(7);
      const scope = 'https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/userinfo.email';

      sessionStorage.setItem('googleDriveOAuthState', state);

      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=token&scope=${encodeURIComponent(scope)}&state=${state}&include_granted_scopes=true`;

      window.location.href = authUrl;
    }
  };

  const handleDisconnect = (serviceId) => {
    try {
      if (!serviceId) return;

      const newServices = {
        ...connectedServices,
        [serviceId]: null,
      };
      setConnectedServices(newServices);

      try {
        localStorage.setItem('cloudConnectedServices', JSON.stringify(newServices));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to save to localStorage:', e);
      }

      const serviceName = serviceId === 'googledrive' ? 'Google Drive' : 'Dropbox';

      setTimeout(() => {
        if (setAppSnackbar && typeof setAppSnackbar === 'function') {
          setAppSnackbar({
            isVisible: true,
            message: `Disconnected from ${serviceName}`,
            type: 'success',
          });
        }
      }, 0);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error disconnecting service:', error);
      setTimeout(() => {
        if (setAppSnackbar && typeof setAppSnackbar === 'function') {
          setAppSnackbar({
            isVisible: true,
            message: 'Failed to disconnect service',
            type: 'error',
          });
        }
      }, 0);
    }
  };

  // Handle OAuth callbacks on component mount
  useEffect(() => {
    handleDropboxOAuthCallback((authData) => {
      handleServiceConnect('dropbox', authData);
    });

    handleGoogleDriveOAuthCallback((authData) => {
      handleServiceConnect('googledrive', authData);
    });
    // eslint-disable-next-line
  }, []);

  const CloudServiceItem = ({ service }) => {
    const IconComponent = service.icon;
    const isConnected = connectedServices[service.id];

    return (
      <div className={styles.serviceCard}>
        <div className={styles.serviceIcon} style={{ color: service.color }}>
          <IconComponent />
        </div>
        <div className={styles.serviceInfo}>
          <h3 className={styles.serviceName}>{service.name}</h3>
          {isConnected && (
            <p className={styles.serviceEmail}>{isConnected.email || 'Connected'}</p>
          )}
          {!service.enabled && (
            <p className={styles.comingSoon}>Coming Soon</p>
          )}
        </div>
        <div className={styles.serviceActions}>
          {service.enabled && !isConnected && (
            <button
              className={styles.connectButton}
              onClick={() => handleConnect(service.id)}
            >
              Connect
            </button>
          )}
          {service.enabled && isConnected && (
            <>
              <div className={styles.connectedBadge}>
                <CheckCircleIcon style={{ fontSize: 18 }} />
                <span>Connected</span>
              </div>
              <button
                className={styles.disconnectButton}
                onClick={() => handleDisconnect(service.id)}
              >
                <LinkOffIcon style={{ fontSize: 16, marginRight: 4 }} />
                Disconnect
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <SettingsPage
      title="Cloud Storage"
      description="Connect your cloud storage accounts to import videos"
    >
      <DataView
        data={[
          {
            labelTitle: langText?.settings?.cloudStorage || 'Connected Services',
            labelDescription: 'Manage your connected cloud storage accounts',
            data: (
              <div className={styles.servicesContainer}>
                {cloudServices.map((service) => (
                  <CloudServiceItem key={service.id} service={service} />
                ))}
              </div>
            ),
          },
        ]}
      />
    </SettingsPage>
  );
};
